import type {EntityDataModel, UnknownObject} from '../../types.ts';
import mergeDataImpl from '../../project_data/mergeDataImpl.ts';
import emptyGuid from '../../lib/emptyGuid.ts';

/*
 * Undo and redo for a whiteboard, kept in the browser for the person's own
 * actions on the board they are looking at.
 *
 * The board has no history anywhere else: every change is an element-level
 * merge streamed to the server and written over the last, so this module
 * remembers enough to replay each action both ways, and the controller sends
 * an undo through the same channel as any other edit. The pieces:
 *
 * - The shadow: this module's own mirror of every element's stored JSON,
 *   seeded from the initial data and kept current from remote changes and
 *   the person's own. It is the "before" for every step (project data lags
 *   a render and is mutated in place, so it can't be), and it tells the
 *   controller which canvas events are its own (an object:added for an id
 *   the shadow already holds is a restore, a remote add or an undo, never
 *   something to broadcast or record).
 * - Steps: add and remove hold the element whole (a row has to be recreated
 *   from it); modify holds the changed keys both ways; order holds the
 *   positions a reorder changed; settings holds the background.
 * - Entries: the steps one action produced, coalesced over a JS tick (a
 *   delete of five things, a group, a drag's final modified plus the bake
 *   that follows it) or over an explicit batch.
 * - Stacks: the last 100 entries, per board, surviving a switch to another
 *   project tab and back (the shadow is re-seeded then), gone on reload.
 *
 * Nothing here touches Fabric or React, so it runs under node --test.
 */

export type ElementJson = UnknownObject;

export type HistoryStep =
    | {kind: 'add'; id: string; json: ElementJson}
    | {kind: 'remove'; id: string; json: ElementJson}
    | {
          kind: 'modify';
          id: string;
          undoPatch: UnknownObject;
          redoPatch: UnknownObject;
      }
    | {
          kind: 'order';
          positions: Record<string, {before?: number; after: number}>;
      }
    | {
          kind: 'settings';
          id: string;
          before: ElementJson | null;
          after: ElementJson;
      };

export type HistoryEntry = HistoryStep[];

// What the controller applies, in order, to carry out an undo or a redo.
export type HistoryOp =
    | {kind: 'add'; id: string; json: ElementJson}
    | {kind: 'remove'; id: string}
    | {kind: 'modify'; id: string; patch: UnknownObject}
    | {kind: 'order'; batch: Record<string, {ordinalPosition: number}>}
    | {kind: 'settings'; id: string; json: ElementJson};

export const HISTORY_LIMIT = 100;

// Fabric writes its version into every object; a 5.3.0 board re-serialised at 7.4.0 is not a change.
const IGNORED_KEYS = new Set(['version']);

const CLOSING_METAS = new Set([
    'object:added',
    'object:modified',
    'object:removed'
]);

export const deepClone = <T>(value: T): T =>
    value === undefined ? value : JSON.parse(JSON.stringify(value));

export const deepEqual = (a: unknown, b: unknown): boolean => {
    if (a === b) {
        return true;
    }
    if (a === null || b === null || a === undefined || b === undefined) {
        // JSON has no undefined; a missing key and a null one read the same.
        return (a ?? null) === (b ?? null);
    }
    if (Array.isArray(a) || Array.isArray(b)) {
        if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
            return false;
        }
        return a.every((item, i) => deepEqual(item, b[i]));
    }
    if (typeof a === 'object' && typeof b === 'object') {
        const left = a as UnknownObject;
        const right = b as UnknownObject;
        const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
        for (const key of keys) {
            if (!deepEqual(left[key], right[key])) {
                return false;
            }
        }
        return true;
    }
    return false;
};

/*
 * The keys whose values differ, with `to`'s value (null for a key `to`
 * doesn't have: the merge on the server and in the SPA never removes a key,
 * and null is how every reader treats "not there"). Nested objects are sent
 * whole, which is what the merge needs to put them back exactly.
 */
export const diffPatch = (
    from: ElementJson,
    to: ElementJson
): UnknownObject => {
    const patch: UnknownObject = {};
    const keys = new Set([...Object.keys(from), ...Object.keys(to)]);

    for (const key of keys) {
        if (IGNORED_KEYS.has(key)) {
            continue;
        }
        if (!deepEqual(from[key], to[key])) {
            patch[key] = to[key] === undefined ? null : deepClone(to[key]);
        }
    }

    return patch;
};

const merged = (base: ElementJson | undefined, partial: UnknownObject) => {
    const next = base ? deepClone(base) : {};
    mergeDataImpl(deepClone(partial), next);
    return next;
};

type Pending = {
    key: string;
    id: string;
    kind: 'element' | 'order' | 'settings';
    before: ElementJson | null;
    after: ElementJson | null;
    positions?: Record<string, {before?: number; after: number}>;
};

type Stacks = {undo: HistoryEntry[]; redo: HistoryEntry[]};

export class WhiteboardHistory {
    private shadow = new Map<string, ElementJson>();
    private pending = new Map<string, Pending>();
    private flushQueued = false;
    private batchDepth = 0;
    private suspended = 0;
    private readonly stacks: Stacks;
    private readonly onChange: () => void;

    // Node runs these files with its types stripped, which rules out constructor parameter properties.
    constructor(stacks: Stacks, onChange: () => void = () => {}) {
        this.stacks = stacks;
        this.onChange = onChange;
    }

    // --- The shadow -------------------------------------------------------

    seed(data: EntityDataModel) {
        this.shadow = new Map(
            Object.entries(data).map(([id, json]) => [id, deepClone(json)])
        );
    }

    has(id: string): boolean {
        return this.shadow.has(id);
    }

    get(id: string): ElementJson | undefined {
        return this.shadow.get(id);
    }

    findSettings(): [string, ElementJson] | null {
        for (const [id, json] of this.shadow) {
            if (json.type === 'settings') {
                return [id, json];
            }
        }
        return null;
    }

    // A change from someone else (or an agent, or the server echoing a large object): the shadow follows, the stacks don't.
    applyRemote(elementId: string, data: UnknownObject) {
        if (elementId === emptyGuid) {
            for (const [id, partial] of Object.entries(data)) {
                const existing = this.shadow.get(id);
                if (existing && partial && typeof partial === 'object') {
                    this.shadow.set(
                        id,
                        merged(existing, partial as UnknownObject)
                    );
                }
            }
            return;
        }

        this.shadow.set(elementId, merged(this.shadow.get(elementId), data));
    }

    applyRemoteRemove(elementId: string) {
        this.shadow.delete(elementId);
    }

    // --- Recording --------------------------------------------------------

    get canUndo(): boolean {
        return this.stacks.undo.length > 0 || this.pending.size > 0;
    }

    get canRedo(): boolean {
        return this.stacks.redo.length > 0;
    }

    // Nothing recorded while fn runs (applying an undo, say).
    suspend<T>(fn: () => T): T {
        this.suspended++;
        try {
            return fn();
        } finally {
            this.suspended--;
        }
    }

    // Steps recorded until endBatch become one entry, however many ticks they take.
    beginBatch() {
        this.batchDepth++;
    }

    endBatch() {
        this.batchDepth = Math.max(0, this.batchDepth - 1);
        if (this.batchDepth === 0) {
            this.flush();
        }
    }

    /*
     * An element the person changed, as the canvas event that closed the
     * change reports it: state is the whole element (null for a remove), or
     * a partial when partial is true (a multi-select drag reports each
     * child's geometry). Events in the middle of a gesture (moving, scaling)
     * record nothing, so the before at the close is the pre-gesture state.
     */
    recordElement(
        id: string,
        meta: string | null | undefined,
        state: ElementJson | null,
        partial = false
    ) {
        if (this.suspended > 0 || !meta || !CLOSING_METAS.has(meta)) {
            return;
        }

        const before = this.shadow.get(id);

        if (state === null) {
            if (!before) {
                return;
            }
            this.shadow.delete(id);
            this.push(id, 'element', before, null);
            return;
        }

        if (meta === 'object:added' && before) {
            // A restore, a remote add or an undo landing on the canvas: already known.
            return;
        }

        if (partial && !before) {
            return;
        }

        const after = partial ? merged(before, state) : deepClone(state);
        this.shadow.set(id, after);
        this.push(id, 'element', before ?? null, after);
    }

    // A reorder: the positions the batch sets, against what the shadow had.
    recordOrder(batch: Record<string, {ordinalPosition: number}>) {
        if (this.suspended > 0) {
            return;
        }

        const positions: Record<string, {before?: number; after: number}> = {};

        for (const [id, change] of Object.entries(batch)) {
            const existing = this.shadow.get(id);
            if (!existing) {
                continue;
            }
            const before = existing.ordinalPosition;
            positions[id] = {
                before: typeof before === 'number' ? before : undefined,
                after: change.ordinalPosition
            };
            this.shadow.set(id, {
                ...existing,
                ordinalPosition: change.ordinalPosition
            });
        }

        if (Object.keys(positions).length === 0) {
            return;
        }

        const pending = this.pending.get('order');
        if (pending?.positions) {
            for (const [id, position] of Object.entries(positions)) {
                const earlier = pending.positions[id];
                pending.positions[id] = {
                    before: earlier ? earlier.before : position.before,
                    after: position.after
                };
            }
        } else {
            this.pending.set('order', {
                key: 'order',
                id: '',
                kind: 'order',
                before: null,
                after: null,
                positions
            });
        }
        this.queueFlush();
    }

    // The background colour: the settings element, whole.
    recordSettings(id: string, after: ElementJson) {
        if (this.suspended > 0) {
            return;
        }
        const before = this.shadow.get(id) ?? null;
        this.shadow.set(id, deepClone(after));
        this.push(id, 'settings', before, deepClone(after));
    }

    private push(
        id: string,
        kind: 'element' | 'settings',
        before: ElementJson | null,
        after: ElementJson | null
    ) {
        const key = `${kind}:${id}`;
        const pending = this.pending.get(key);

        if (pending) {
            // First before, last after: a bake's second modified wins.
            pending.after = after;
        } else {
            this.pending.set(key, {key, id, kind, before, after});
        }
        this.queueFlush();
    }

    private queueFlush() {
        if (this.batchDepth > 0 || this.flushQueued) {
            return;
        }
        this.flushQueued = true;
        queueMicrotask(() => {
            this.flushQueued = false;
            if (this.batchDepth === 0) {
                this.flush();
            }
        });
    }

    // Packages what has been recorded into one entry.
    flush() {
        if (this.pending.size === 0) {
            return;
        }

        const entry: HistoryEntry = [];

        for (const pending of this.pending.values()) {
            const step = this.toStep(pending);
            if (step) {
                entry.push(step);
            }
        }
        this.pending.clear();

        if (entry.length === 0) {
            return;
        }

        this.stacks.undo.push(entry);
        if (this.stacks.undo.length > HISTORY_LIMIT) {
            this.stacks.undo.splice(0, this.stacks.undo.length - HISTORY_LIMIT);
        }
        this.stacks.redo.length = 0;
        this.onChange();
    }

    private toStep(pending: Pending): HistoryStep | null {
        if (pending.kind === 'order') {
            const positions = Object.fromEntries(
                Object.entries(pending.positions ?? {}).filter(
                    ([, p]) => p.before !== p.after
                )
            );
            return Object.keys(positions).length > 0
                ? {kind: 'order', positions}
                : null;
        }

        if (pending.kind === 'settings') {
            return pending.after && !deepEqual(pending.before, pending.after)
                ? {
                      kind: 'settings',
                      id: pending.id,
                      before: pending.before,
                      after: pending.after
                  }
                : null;
        }

        const {id, before, after} = pending;
        if (before && after) {
            const undoPatch = diffPatch(after, before);
            const redoPatch = diffPatch(before, after);
            return Object.keys(undoPatch).length > 0
                ? {kind: 'modify', id, undoPatch, redoPatch}
                : null;
        }
        if (after) {
            return {kind: 'add', id, json: after};
        }
        if (before) {
            return {kind: 'remove', id, json: before};
        }
        return null;
    }

    // --- Undo and redo ----------------------------------------------------

    undo(): HistoryOp[] {
        this.flush();
        const entry = this.stacks.undo.pop();
        if (!entry) {
            return [];
        }
        const ops = [...entry]
            .reverse()
            .flatMap(step => this.invert(step, true));
        this.stacks.redo.push(entry);
        this.onChange();
        return ops;
    }

    redo(): HistoryOp[] {
        this.flush();
        const entry = this.stacks.redo.pop();
        if (!entry) {
            return [];
        }
        const ops = entry.flatMap(step => this.invert(step, false));
        this.stacks.undo.push(entry);
        this.onChange();
        return ops;
    }

    /*
     * The ops that undo (or redo) one step against the board as it is now,
     * applied to the shadow as they are made. Three-way: a key someone else
     * has changed since is left alone, and an element they deleted stays
     * deleted.
     */
    private invert(step: HistoryStep, undo: boolean): HistoryOp[] {
        switch (step.kind) {
            case 'add':
            case 'remove': {
                const removing = (step.kind === 'add') === undo;
                if (removing) {
                    if (!this.shadow.has(step.id)) {
                        return [];
                    }
                    this.shadow.delete(step.id);
                    return [{kind: 'remove', id: step.id}];
                }
                if (this.shadow.has(step.id)) {
                    return [];
                }
                this.shadow.set(step.id, deepClone(step.json));
                return [{kind: 'add', id: step.id, json: deepClone(step.json)}];
            }

            case 'modify': {
                const current = this.shadow.get(step.id);
                if (!current) {
                    return [];
                }
                const apply = undo ? step.undoPatch : step.redoPatch;
                const expected = undo ? step.redoPatch : step.undoPatch;
                const patch: UnknownObject = {};

                for (const [key, value] of Object.entries(apply)) {
                    if (deepEqual(current[key], expected[key])) {
                        patch[key] = deepClone(value);
                    }
                }

                if (Object.keys(patch).length === 0) {
                    return [];
                }
                this.shadow.set(step.id, merged(current, patch));
                return [{kind: 'modify', id: step.id, patch}];
            }

            case 'order': {
                const batch: Record<string, {ordinalPosition: number}> = {};

                for (const [id, position] of Object.entries(step.positions)) {
                    const target = undo ? position.before : position.after;
                    const existing = this.shadow.get(id);
                    if (typeof target !== 'number' || !existing) {
                        continue;
                    }
                    batch[id] = {ordinalPosition: target};
                    this.shadow.set(id, {...existing, ordinalPosition: target});
                }

                return Object.keys(batch).length > 0
                    ? [{kind: 'order', batch}]
                    : [];
            }

            case 'settings': {
                const json = undo
                    ? step.before ?? {
                          type: 'settings',
                          version: '',
                          elementId: step.id,
                          canvasBackgroundColor: ''
                      }
                    : step.after;
                this.shadow.set(step.id, deepClone(json));
                return [{kind: 'settings', id: step.id, json: deepClone(json)}];
            }
        }
    }
}

// One pair of stacks per board, for as long as the page lives.
const stores = new Map<string, Stacks>();

export const getWhiteboardHistory = (
    key: string,
    onChange?: () => void
): WhiteboardHistory => {
    let stacks = stores.get(key);
    if (!stacks) {
        stacks = {undo: [], redo: []};
        stores.set(key, stacks);
    }
    return new WhiteboardHistory(stacks, onChange);
};
